// @flow
import React, {Fragment} from 'react';

import {
  getObjectActivityURL,
  getObjectReviewURL,
  getObjectTasksURL,
  getObjectTeamsURL,
  getObjectPoliciesURL,
  getProcessCommentsURL,
  getObjectCurrentVersionURL,
  getObjectSignoffURL,
} from 'app/urls';
import {gt} from 'app/utils/i18n';
import {DocumentTeamsSidebar} from 'app/components/general/DocumentTeamsSidebar';
import {getContentType} from 'app/utils/content-type';
import {getPathFromURL} from 'app/utils/urls';
import {PageSidebarStructure} from 'app/general/structures/PageSidebarStructure';
import {SidebarItem} from 'app/general/structures/SidebarItem';
import {DetailPageRelatedTasksSidebar} from 'app/components/general/DetailPageRelatedTasksSidebar';
import {DetailPageActivitySidebar} from 'app/components/general/DetailPageActivitySidebar';
import {ProcessDetailPageReviewSidebar} from 'app/components/general/DetailPageReviewSidebar';
import {GenericAcceptSidebar} from 'app/acceptance/components/GenericAcceptSidebar';
import {ProcessVersionsSidebar} from './ProcessVersionsSidebar';
import {ProcessPolicieSidebar} from './ProcessPoliciesSidebar';
import {GenericCommentsSidebar} from 'app/comments/components/GenericCommentsSidebar';
import type {contentArgsType} from 'app/activity/types';
import type {AcceptanceType} from 'app/acceptance/types';
import {type ProcessType} from 'app/process/types';
import VersionsSidebarItem from 'app/procedures/components/ProcedureDetailPage/ProcedurePageSidebar/VersionsSidebarItem';
import type {ProcessVersionType} from 'app/process/types';
import {DetailPageSidebarShortcuts} from 'app/general/shortcuts/DetailPageSidebarShortcuts';
import {CommentsSidebarItem} from 'app/comments/components/CommentsSidebarItem';
import {AcceptanceSidebarItem} from 'app/acceptance/components/AcceptanceSidebarItem';
import {DetailPageSignoffSidebar} from 'app/components/general/DetailPageSignoffSidebar';
import {addProcesses, removeItem} from 'app/teams/actions';

const policyIcon = getContentType('policy').icon;
const teamIcon = getContentType('team').icon;

type Props = {
  process: ProcessType,
  fetchProcess: () => Promise<any>,
  versionId?: string,
  userId?: number,
  fetchProcessVersions: () => Promise<any>,
  version?: ProcessVersionType,
  versions: Array<any>,
  acceptance?: AcceptanceType,
  router: routerShape,
  route: any,
};

type State = {
  collapsed: boolean,
};

export class ProcessPageSidebar extends React.PureComponent<Props, State> {
  state = {collapsed: false};
  contentArgs: contentArgsType;

  constructor() {
    super();
    this.contentArgs = {target: gt('this process')};
  }

  toggleCollapsed = () => {
    this.setState({collapsed: !this.state.collapsed});
  };

  toggleVersionsList = () => {
    if (this.state.collapsed) {
      this.toggleCollapsed();
    }
  };

  getVersionsURL = (): string => {
    const {process, versionId, router} = this.props;
    return getPathFromURL(
      getObjectCurrentVersionURL(process, versionId, router.location.search)
    );
  };

  getTasksURL = (): string => {
    const {process, router} = this.props;
    return getObjectTasksURL(process, router.location.search);
  };

  getCommentsURL = (): string => {
    const {process, versionId, router} = this.props;
    return getProcessCommentsURL(process, versionId, router.location.search);
  };

  getActivityURL = (): string => {
    const {process, router} = this.props;
    return getObjectActivityURL(process, router.location.search);
  };

  getPoliciesURL = (): string => {
    const {process, router} = this.props;
    return getObjectPoliciesURL(process, router.location.search);
  };

  getTeamUrl = (): string => {
    const {process, router} = this.props;
    return getObjectTeamsURL(process, router.location.search);
  };

  getReviewURL = (): string => {
    const {process, router} = this.props;
    return getObjectReviewURL(process, router.location.search);
  };

  closeInnerSidebar = () => {
    const {router, process} = this.props;
    router.replace(process.html_url);
  };

  goToEditURL = () => {
    const {process} = this.props;
    this.props.router.push(`${process.html_url}edit/`);
  };

  getSignoffURL = (): string => {
    const {process, router} = this.props;
    return getObjectSignoffURL(process, router.location.search);
  };

  render() {
    const {
      process,
      router,
      route,
      fetchProcess,
      version,
      fetchProcessVersions,
      versionId,
      versions,
    } = this.props;
    const {
      showAccept,
      showTasks,
      showActivity,
      showVersions,
      showTeams,
      showPolicies,
      showComments,
      showReview,
      showSignoff,
    } = route.props;
    const {collapsed} = this.state;

    const processPath = getPathFromURL(process.html_url);

    return (
      <DetailPageSidebarShortcuts
        openVersions={() => this.props.router.push(this.getVersionsURL())}
        openComments={() => this.props.router.push(this.getCommentsURL())}
        openActivities={() => this.props.router.push(this.getActivityURL())}
        edit={this.goToEditURL}
      >
        <PageSidebarStructure
          innerContent={
            showTasks ? (
              <DetailPageRelatedTasksSidebar
                router={router}
                toggleSidebar={this.closeInnerSidebar}
                objectId={process.id}
                contentType="proceduregroup"
                onChange={fetchProcess}
                canAssign={process.can_assign}
              />
            ) : showComments === true && versionId != null ? (
              <GenericCommentsSidebar
                contentType="proceduregroup"
                mentionsContentType="proceduregroup"
                addContentType="processversion"
                objectId={process.id}
                versionId={versionId}
                canDeleteComment={process.can_edit}
                toggleSidebar={this.closeInnerSidebar}
                loadAdditionalData={fetchProcessVersions}
              />
            ) : showActivity ? (
              <DetailPageActivitySidebar
                title={gt('Process Activity')}
                objectId={process.id}
                showExportButton={process.can_edit}
                contentType="proceduregroup"
                contentArgs={this.contentArgs}
                toggleSidebar={this.closeInnerSidebar}
              />
            ) : showPolicies ? (
              <ProcessPolicieSidebar
                onClose={this.closeInnerSidebar}
                canEdit={process.can_edit}
                showFilterField={process.can_edit}
                processURL={process.url}
                processId={process.id}
                loadItems={fetchProcess}
                items={process.policies}
              />
            ) : showTeams ? (
              <DocumentTeamsSidebar
                contentType="process"
                onClose={this.closeInnerSidebar}
                add={addProcesses}
                remove={removeItem}
                canEdit={process.can_edit}
                url={process.url}
                id={process.id}
                fetch={fetchProcess}
                team_memberships={process.team_memberships}
                teams={process.team_names}
              />
            ) : showVersions ? (
              <ProcessVersionsSidebar
                canDelete={process.can_delete}
                selectedVersion={version}
                closeSidebar={this.closeInnerSidebar}
                objectId={process.id}
                collapsed={collapsed}
                onToggleCollapsed={this.toggleCollapsed}
              />
            ) : showReview === true && process.current_version != null ? (
              <ProcessDetailPageReviewSidebar
                title={gt('Process Review')}
                // $FlowFixMe - no idea the reason Flow doesn't accept the correct object type here
                object={process}
                contentType="proceduregroup"
                versionId={process.current_version.id}
                versionContentType={'processversion'}
                canReview={process.can_edit}
                onToggleReview={fetchProcess}
                onReview={fetchProcess}
                toggleSidebar={this.closeInnerSidebar}
              />
            ) : showAccept && this.props.version != null ? (
              <GenericAcceptSidebar
                acceptance={this.props.acceptance}
                versionURL={this.props.version.url}
                versionApprovedBy={this.props.version.approved_by}
                versionApprovedAt={this.props.version.approved_at}
                userId={this.props.userId}
                documentId={process.id}
                documentType={process.content_type}
                documentHtmlURL={process.html_url}
                toggleSidebar={this.closeInnerSidebar}
                nextReviewDate={process.next_review_at}
              />
            ) : showSignoff ? (
              <DetailPageSignoffSidebar
                onClose={this.closeInnerSidebar}
                title={gt('Sign Off Log')}
                document={process}
                refreshFunc={fetchProcess}
              />
            ) : null
          }
        >
          {(showTooltip, hideTooltip) => {
            const commonProps = {
              forceTooltipShow: showTooltip,
              onClick: hideTooltip,
            };
            return (
              <Fragment>
                <SidebarItem
                  title={gt('See the process')}
                  icon="far fa-copy"
                  onlyActiveOnIndex={true}
                  link={processPath}
                  {...commonProps}
                />
                <VersionsSidebarItem
                  link={this.getVersionsURL()}
                  collapsed={collapsed}
                  onClick={this.toggleVersionsList}
                  active={showVersions}
                  versions={versions}
                  {...commonProps}
                />
                <AcceptanceSidebarItem
                  document={process}
                  contentType="proceduregroup"
                  location={router.location}
                  {...commonProps}
                />
                <CommentsSidebarItem
                  contentType={'proceduregroup'}
                  objectId={process.id}
                  active={showComments}
                  link={this.getCommentsURL}
                  {...commonProps}
                />
                <SidebarItem
                  title={gt('Related tasks')}
                  active={showTasks}
                  icon="fa fa-tasks"
                  badge={
                    process.task_count != null && process.task_count > 0
                      ? process.task_count
                      : undefined
                  }
                  link={this.getTasksURL}
                  {...commonProps}
                />
                <SidebarItem
                  title={gt('Policies')}
                  active={showPolicies}
                  icon={policyIcon}
                  link={this.getPoliciesURL}
                  {...commonProps}
                />
                <SidebarItem
                  title={gt('Teams')}
                  active={showTeams}
                  icon={teamIcon}
                  link={this.getTeamUrl}
                  {...commonProps}
                />
                <SidebarItem
                  title={gt('Process Activity')}
                  active={showActivity}
                  icon="fa fa-signal"
                  link={this.getActivityURL}
                  {...commonProps}
                />
                <SidebarItem
                  disabled={process.approved_at == null}
                  disabledTooltipText={gt(
                    'Only an approved process can be reviewed.'
                  )}
                  title={gt('Review')}
                  active={showReview}
                  icon="fa fa-certificate"
                  link={this.getReviewURL}
                  {...commonProps}
                />
                <SidebarItem
                  icon="fa fa-signature"
                  title={gt('Sign Off Log')}
                  active={showSignoff}
                  link={this.getSignoffURL}
                  {...commonProps}
                />
              </Fragment>
            );
          }}
        </PageSidebarStructure>
      </DetailPageSidebarShortcuts>
    );
  }
}
